import Dexie from 'dexie';
import type { EntityTable } from 'dexie';

import type {
  LocalAnaestheticCase,
  LocalCase,
  LocalCommunication,
  LocalEdtEntry,
  LocalProcedure,
} from '@iculogbook/shared';

/**
 * The local database. This — not the server — is the app's source of truth
 * while a trainee is using it.
 *
 * Every write lands here first and returns immediately; the sync engine drains
 * to the server afterwards. The UI must never await the network, because the
 * app is used at 3am on a phone in a side room with no signal, and an entry
 * lost to a failed request is the reputational failure that would end the
 * project.
 *
 * One database for all users, with `userId` on every record and a full wipe on
 * sign-out. Reads are scoped by `userId` regardless — hazard H7 in
 * COMPLIANCE.md is one trainee seeing another's logbook, and belt and braces is
 * the right posture for the most severe hazard in the log.
 */

export type OutboxStatus = 'pending' | 'failed';

/**
 * One queued change. Soft deletes travel as upserts, because a delete is just
 * an edit that sets `deletedAt` — the server has no hard-delete path for
 * entries at all.
 */
export type OutboxItem = {
  seq?: number;
  op: 'upsert';
  entity: 'case' | 'procedure' | 'edt' | 'anaesthetic' | 'communication';
  entityId: string;
  userId: string;
  status: OutboxStatus;
  /** Consecutive failures. Ten marks it failed and surfaces it in Settings. */
  attempts: number;
  lastError?: string;
  createdAt: Date;
};

/** Small key/value store for things that are not entries. */
export type MetaRecord = {
  key: string;
  value: unknown;
};

export class ICULogbookDatabase extends Dexie {
  declare cases: EntityTable<LocalCase, 'id'>;
  declare procedures: EntityTable<LocalProcedure, 'id'>;
  declare edtEntries: EntityTable<LocalEdtEntry, 'id'>;
  declare anaestheticCases: EntityTable<LocalAnaestheticCase, 'id'>;
  declare communications: EntityTable<LocalCommunication, 'id'>;
  declare outbox: EntityTable<OutboxItem, 'seq'>;
  declare meta: EntityTable<MetaRecord, 'key'>;

  constructor(name = 'iculogbook') {
    super(name);

    /*
     * Indexes are exactly those in ARCHITECTURE.md.
     *
     * `deletedAt` is indexed but cannot be used to find live records: IndexedDB
     * does not index undefined, so soft-deleted rows are the only ones that
     * appear in it. Live reads use [userId+occurredAt] and filter in memory.
     * That is fine — a seven-year logbook is roughly 6,000 entries, which is
     * small, and ARCHITECTURE.md's performance budget says to load it all
     * rather than build pagination.
     */
    this.version(1).stores({
      cases: 'id, occurredAt, updatedAt, deletedAt, [userId+occurredAt]',
      procedures: 'id, occurredAt, updatedAt, caseId, deletedAt, [userId+occurredAt]',
      outbox: '++seq, status, entityId',
      meta: 'key',
    });

    /*
     * Version 2 adds EDT entries. A Dexie version is a delta — the tables not
     * named here keep the schema version 1 gave them, and an existing database
     * upgrades in place without touching a single stored row. There is no
     * migration function because there is nothing to migrate: this is a new
     * table, and every field on it is new.
     *
     * No `caseId`: an EDT entry belongs to nothing. Indexes otherwise match
     * cases, because the reads are the same ones.
     */
    this.version(2).stores({
      edtEntries: 'id, occurredAt, updatedAt, deletedAt, [userId+occurredAt]',
    });

    /*
     * Version 3 adds anaesthetic cases, imported from the RCoA platform. Same
     * delta rule as version 2: one new table, nothing existing touched, and no
     * migration function because every field on it is new.
     */
    this.version(3).stores({
      anaestheticCases: 'id, occurredAt, updatedAt, deletedAt, importBatchId, [userId+occurredAt]',
    });

    /*
     * Version 4 adds Communications — significant discussions, most often
     * withdrawal or withholding of treatment. Same delta rule as versions 2
     * and 3: one new table, nothing existing touched, no migration function.
     * Standalone in this phase, like EDT entries — no `caseId`, so the index
     * shape matches `edtEntries` rather than `procedures`.
     */
    this.version(4).stores({
      communications: 'id, occurredAt, updatedAt, deletedAt, [userId+occurredAt]',
    });
  }
}

export const db = new ICULogbookDatabase();
